import { useMemo, useState } from 'react';
import { useStore } from '@/store';
import { abilities } from '@/data';
import { abilityScoreFromXp, xpForAbilityScore } from '@/lib/scores';
import { SearchField } from '@/components/ui/SearchField';
import { InfoHint } from '@/components/ui/InfoHint';

// Some specializations from the manuscript transform contain Obsidian-style
// embeds (`![[...]]`) or markdown tables. Strip those so they don't leak
// into the UI; keep only the first short phrase.
function cleanSpecs(specs: string[]): string {
  return specs
    .map((s) => s.split('\n')[0].replace(/!\[\[[^\]]+\]\]/g, '').trim())
    .filter(Boolean)
    .join(', ');
}

export function AbilitiesSection() {
  const c = useStore((s) => s.character!);
  const update = useStore((s) => s.updateCharacter);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return abilities.filter((a) => {
      if (typeFilter !== 'all' && a.type !== typeFilter) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q);
    });
  }, [query, typeFilter]);

  const pickedById = new Map(c.abilities.map((a) => [a.id, a]));

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-center gap-2">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search abilities…"
        />
        <select
          className="input-base w-auto"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All types</option>
          <option value="general">General</option>
          <option value="academic">Academic</option>
          <option value="arcane">Arcane</option>
          <option value="martial">Martial</option>
          <option value="supernatural">Supernatural</option>
        </select>
      </div>

      <section>
        <h3 className="section-title">
          Chosen <span className="text-sm muted">({c.abilities.length})</span>
          <InfoHint>
            <strong>XP → Score is triangular:</strong> score 1 = 5 xp, 2 = 15,
            3 = 30, 4 = 50, 5 = 75. Each +5 XP nudges you toward the next level.
            <br />
            <strong>Specialization</strong> gives +1 when the task fits your
            chosen niche (e.g. <em>Single Weapon (Longsword)</em>).
            <br />
            Academic, Arcane, Martial, and Supernatural Abilities need a Virtue
            to permit them — magi get Academic + Arcane free via apprenticeship.
          </InfoHint>
        </h3>
        <div className="space-y-1">
          {c.abilities.length === 0 && (
            <div className="muted text-sm italic">No abilities yet. Pick from the list below.</div>
          )}
          {c.abilities.map((a) => {
            const def = abilities.find((x) => x.id === a.id);
            const score = abilityScoreFromXp(a.xp);
            return (
              <div key={a.id} className="card flex flex-wrap items-center gap-2">
                <div className="flex-1 min-w-[160px]">
                  <div className="font-medium">{def?.name ?? a.id}</div>
                  <div className="text-xs muted">
                    score {score} (xp {a.xp} / next @ {xpForAbilityScore(score + 1)})
                  </div>
                </div>
                <button
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() =>
                    update((cur) => ({
                      ...cur,
                      abilities: cur.abilities.map((x) =>
                        x.id === a.id ? { ...x, xp: Math.max(0, x.xp - 5) } : x
                      ),
                    }))
                  }
                >
                  −5 xp
                </button>
                <button
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() =>
                    update((cur) => ({
                      ...cur,
                      abilities: cur.abilities.map((x) =>
                        x.id === a.id ? { ...x, xp: x.xp + 5 } : x
                      ),
                    }))
                  }
                >
                  +5 xp
                </button>
                <input
                  className="input-base w-32"
                  placeholder="specialization"
                  value={a.specialization ?? ''}
                  onChange={(e) =>
                    update((cur) => ({
                      ...cur,
                      abilities: cur.abilities.map((x) =>
                        x.id === a.id ? { ...x, specialization: e.target.value } : x
                      ),
                    }))
                  }
                />
                <button
                  className="btn-secondary px-2 py-1 text-xs"
                  onClick={() =>
                    update((cur) => ({ ...cur, abilities: cur.abilities.filter((x) => x.id !== a.id) }))
                  }
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="section-title">Available abilities ({filtered.length})</h3>
        <div className="max-h-[420px] overflow-y-auto space-y-1 pr-1">
          {filtered.map((a) => {
            const picked = pickedById.has(a.id);
            return (
              <button
                key={a.source}
                onClick={() => {
                  if (picked) return;
                  update((cur) => ({
                    ...cur,
                    abilities: [...cur.abilities, { id: a.id, xp: 0 }],
                  }));
                }}
                disabled={picked}
                className={`w-full text-left rounded-md border px-3 py-2 text-sm transition-colors ${
                  picked
                    ? 'border-parchment-200/30 bg-parchment-200/5 opacity-60 cursor-default'
                    : 'border-ink-700 hover:border-parchment-300/40'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{a.name}</span>
                  {a.type && <span className="chip capitalize">{a.type}</span>}
                </div>
                {a.specializations.length > 0 && (
                  <div className="muted text-xs">{cleanSpecs(a.specializations)}</div>
                )}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
